<template>
  <v-container fluid class="pa-0">
    <!-- Top bar -->
    <v-toolbar dense flat color="grey lighten-3">
      <v-toolbar-title>Campaign Edit</v-toolbar-title>
      <v-spacer />
      <v-btn
        :loading="loading"
        :disabled="loading"
        color="primary"
        dark
        @click="submit"
      >
        Update
      </v-btn>
      <v-btn :disabled="loading" color="primary" text @click="close">
        Cancel
      </v-btn>
    </v-toolbar>

    <v-container fluid class="touch-view-container">
      <v-row dense>
        <!-- LEFT: main -->
        <v-col cols="12" md="8">
          <v-card outlined class="card-soft">
            <v-card-text class="pa-0">
              <v-tabs v-model="tab" background-color="grey lighten-4" grow>
                <v-tab>Basics</v-tab>
                <v-tab>Settings</v-tab>
                <v-tab v-if="Campaign.type === 'sponsor'">Details</v-tab>
              </v-tabs>

              <v-divider />

              <v-tabs-items v-model="tab">
                <!-- BASICS -->
                <v-tab-item>
                  <v-card-text class="pa-4">
                    <v-row dense>
                      <v-col cols="12">
                        <div class="d-flex align-center flex-wrap">
                          <div class="font-weight-bold mr-3">Donation Type</div>

                          <v-btn-toggle
                            active-class="custom-active-class"
                            mandatory
                            dense
                            v-model="Campaign.type"
                            color="primary"
                            group
                          >
                            <v-btn value="donation" outlined class="ma-1">
                              Donation
                            </v-btn>
                            <v-btn value="sponsor" outlined class="ma-1">
                              Sponsor
                            </v-btn>
                          </v-btn-toggle>

                          <v-spacer />

                          <div class="d-flex align-center">
                            <span class="font-weight-bold mr-2">Publish</span>
                            <v-switch
                              inset
                              v-model="Campaign.isActive"
                              class="ma-0"
                            />
                          </div>
                        </div>
                      </v-col>

                      <v-col cols="12">
                        <v-text-field
                          outlined
                          dense
                          autofocus
                          v-model="Campaign.name"
                          label="Title"
                          :hide-details="nameErrors.length === 0"
                          :error-messages="nameErrors"
                        />
                      </v-col>

                      <v-col cols="12">
                        <div
                          class="text-caption grey--text text--darken-1 mb-2"
                        >
                          Description
                        </div>
                        <quill-editor
                          :content="Campaign.description"
                          :options="editorOption"
                          @change="onEditorChange($event)"
                        />
                      </v-col>

                      <v-col cols="12" class="mt-2">
                        <div class="d-flex align-center mb-2">
                          <div class="font-weight-bold">Banner Image</div>
                          <v-spacer />
                          <div class="text-caption grey--text text--darken-1">
                            Suggested: 1024 x 400
                          </div>
                        </div>

                        <v-card
                          class="upload-card"
                          outlined
                          hover
                          @click="pickFile"
                        >
                          <div v-if="Campaign.url" class="upload-preview">
                            <v-img :src="Campaign.url" height="180" />
                          </div>

                          <div v-else class="upload-placeholder">
                            <v-icon size="42" class="mb-2"
                              >mdi-image-plus</v-icon
                            >
                            <div class="font-weight-bold">Upload banner</div>
                            <div class="text-caption grey--text text--darken-1">
                              Click to choose an image
                            </div>
                          </div>
                        </v-card>

                        <input
                          type="file"
                          style="display: none"
                          ref="image"
                          accept="image/*"
                          @change="onFilePicked"
                        />
                      </v-col>

                      <v-col cols="12" class="mt-2">
                        <div class="font-weight-bold mb-2">Category</div>

                        <v-select
                          outlined
                          dense
                          :items="DonationCategories"
                          item-text="name"
                          item-value="id"
                          v-model="Campaign.DefaultDonationCategoryId"
                          label="Select Category"
                          :hide-details="DonationIdErrors.length === 0"
                          :error-messages="DonationIdErrors"
                          :loading="loadingCategories"
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-tab-item>

                <!-- SETTINGS -->
                <v-tab-item>
                  <v-card-text class="pa-4">
                    <!-- Donation settings -->
                    <div v-if="Campaign.type === 'donation'">
                      <v-row dense>
                        <v-col cols="12" md="8">
                          <div class="font-weight-bold">Goal Amount</div>
                          <div class="text-body-2 grey--text text--darken-1">
                            This does not stop donations, it only shows
                            progress.
                          </div>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            outlined
                            dense
                            type="number"
                            v-model="Campaign.dontationGoal"
                            label="Donation Goal"
                          />
                        </v-col>

                        <v-col cols="12">
                          <v-divider class="my-2" />
                        </v-col>

                        <v-col cols="12" md="8">
                          <div class="font-weight-bold">Show Donate Count</div>
                          <div class="text-body-2 grey--text text--darken-1">
                            Show number of donations made.
                          </div>
                        </v-col>
                        <v-col cols="12" md="4" class="text-right">
                          <v-switch
                            inset
                            v-model="Campaign.metadata.showDonateCount"
                          />
                        </v-col>

                        <v-col cols="12" md="8">
                          <div class="font-weight-bold">
                            Show Visitors Count
                          </div>
                          <div class="text-body-2 grey--text text--darken-1">
                            Show number of page visitors.
                          </div>
                        </v-col>
                        <v-col cols="12" md="4" class="text-right">
                          <v-switch
                            inset
                            v-model="Campaign.metadata.showVisitorsCount"
                          />
                        </v-col>

                        <v-col cols="12" md="8">
                          <div class="font-weight-bold">Show Goal/Funds</div>
                          <div class="text-body-2 grey--text text--darken-1">
                            Show progress bar with raised vs goal.
                          </div>
                        </v-col>
                        <v-col cols="12" md="4" class="text-right">
                          <v-switch
                            inset
                            v-model="Campaign.metadata.showGoalFunds"
                          />
                        </v-col>

                        <v-col cols="12" md="8">
                          <div class="font-weight-bold">
                            Item Donation Limit
                          </div>
                          <div class="text-body-2 grey--text text--darken-1">
                            Lock a donation item when it reaches its target.
                          </div>
                        </v-col>
                        <v-col cols="12" md="4" class="text-right">
                          <v-switch
                            inset
                            v-model="Campaign.metadata.donationLimit"
                          />
                        </v-col>

                        <v-col cols="12">
                          <v-divider class="my-2" />
                        </v-col>

                        <v-col cols="12" md="8">
                          <div class="font-weight-bold">
                            Pre Defined Donation Amounts
                          </div>
                          <div class="text-body-2 grey--text text--darken-1">
                            Add preset amounts like 10, 50, 100.
                          </div>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-btn color="success" block @click="addValue">
                            <v-icon left>mdi-plus</v-icon>
                            Add Amount
                          </v-btn>
                        </v-col>

                        <v-col cols="12">
                          <div class="d-flex flex-wrap">
                            <v-chip
                              v-for="(item, index) in preDefinedValue"
                              :key="index"
                              class="mr-2 mb-2"
                              label
                              close
                              @click:close="removeValue(index)"
                            >
                              {{ item | currency }}
                            </v-chip>

                            <div
                              v-if="preDefinedValue.length === 0"
                              class="text-caption grey--text text--darken-1"
                            >
                              No predefined amounts yet.
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                    </div>

                    <!-- Sponsor settings -->
                    <div v-else>
                      <v-row dense>
                        <v-col cols="12" md="6">
                          <v-select
                            dense
                            outlined
                            label="Sponsor Style"
                            :items="[
                              { text: 'Fast Collection', value: 'fast' },
                              { text: 'Student Sponsorship', value: 'student' },
                              { text: 'Unit Collection', value: 'units' },
                              { text: 'Normal (Simple)', value: 'normal' },
                            ]"
                            v-model="Campaign.metadata.detailStyle"
                          />
                        </v-col>

                        <v-col
                          cols="12"
                          md="6"
                          v-if="Campaign.metadata.detailStyle !== 'units'"
                        >
                          <v-select
                            dense
                            outlined
                            :items="durationTypes"
                            v-model="Campaign.metadata.durationType"
                            label="Duration Type"
                          />
                        </v-col>

                        <v-col cols="12">
                          <v-divider class="my-2" />
                        </v-col>

                        <v-col cols="12" md="8">
                          <div class="font-weight-bold">
                            Item Donation Limit
                          </div>
                          <div class="text-body-2 grey--text text--darken-1">
                            For sponsor too, you can lock items when full.
                          </div>
                        </v-col>
                        <v-col cols="12" md="4" class="text-right">
                          <v-switch
                            inset
                            v-model="Campaign.metadata.donationLimit"
                          />
                        </v-col>
                      </v-row>
                    </div>
                  </v-card-text>
                </v-tab-item>

                <!-- DETAILS -->
                <v-tab-item v-if="Campaign.type === 'sponsor'">
                  <v-card-text class="pa-4">
                    <div class="d-flex align-center mb-3">
                      <div class="font-weight-bold text-subtitle-1">
                        Campaign Items
                      </div>

                      <v-spacer />

                      <v-btn
                        v-if="Campaign.metadata.detailStyle === 'fast'"
                        color="primary"
                        outlined
                        class="mr-2"
                        @click="applyTemplate('fast')"
                      >
                        <v-icon left>mdi-calendar-month</v-icon>
                        Reset Fast Template
                      </v-btn>

                      <v-btn
                        v-if="Campaign.metadata.detailStyle === 'student'"
                        color="primary"
                        outlined
                        class="mr-2"
                        @click="applyTemplate('student')"
                      >
                        <v-icon left>mdi-account-school</v-icon>
                        Add Student Row
                      </v-btn>

                      <v-btn
                        v-if="Campaign.metadata.detailStyle === 'units'"
                        color="primary"
                        outlined
                        class="mr-2"
                        @click="applyTemplate('units')"
                      >
                        <v-icon left>mdi-cube</v-icon>
                        Add Unit Row
                      </v-btn>

                      <v-chip small color="grey lighten-3">
                        {{ (Campaign.CampaignDetails || []).length }} rows
                      </v-chip>
                    </div>

                    <CampaignDetails
                      :detailStyle="Campaign.metadata.detailStyle || 'normal'"
                      @submit="addDetail"
                    />

                    <v-divider class="my-3" />

                    <v-data-table
                      :headers="detailHeaders"
                      :items="Campaign.CampaignDetails"
                      hide-default-footer
                      :items-per-page="1000"
                      no-data-text="No items yet"
                      class="elevation-0"
                    >
                      <template v-slot:item="{ item, index }">
                        <tr>
                          <td class="cell-actions">
                            <v-btn icon @click="deleteItem(index)">
                              <v-icon color="red">mdi-delete</v-icon>
                            </v-btn>
                          </td>

                          <td v-for="h in editableKeys" :key="h" class="py-1">
                            <v-text-field
                              v-model="item[h]"
                              outlined
                              dense
                              hide-details
                              :type="fieldType(h)"
                              @focus="$event.target.select()"
                            />
                          </td>
                        </tr>
                      </template>
                    </v-data-table>

                    <div class="text-caption grey--text text--darken-1 mt-2">
                      Tip: Add/update totalQuantity for seats/units, it will
                      save as capacity.
                    </div>
                  </v-card-text>
                </v-tab-item>
              </v-tabs-items>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- RIGHT: summary -->
        <v-col cols="12" md="4">
          <v-card outlined class="card-soft">
            <v-card-text class="pa-4">
              <div class="text-subtitle-1 font-weight-bold mb-2">Summary</div>

              <div class="mb-2">
                <div class="text-caption grey--text text--darken-1">Type</div>
                <div class="font-weight-bold">{{ Campaign.type || "-" }}</div>
              </div>

              <div class="mb-2">
                <div class="text-caption grey--text text--darken-1">Title</div>
                <div class="font-weight-bold text-truncate">
                  {{ Campaign.name || "-" }}
                </div>
              </div>

              <div class="mb-2">
                <div class="text-caption grey--text text--darken-1">
                  Category
                </div>
                <div class="font-weight-bold">
                  {{ selectedCategoryName }}
                </div>
              </div>

              <v-divider class="my-3" />

              <v-btn
                block
                color="primary"
                dark
                :loading="loading"
                :disabled="loading"
                @click="submit"
              >
                Update Campaign
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import campaignService from "../service.js";
import donationCategoryService from "@/modules/DonationCategory/service";
import CampaignDetails from "@/modules/Campaign/components/Details.vue";

const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");

import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";
import { quillEditor } from "vue-quill-editor";

export default {
  name: "edit-campaign",
  mixins: [validationMixin],
  components: { CampaignDetails, quillEditor },

  validations: {
    Campaign: {
      name: { required },
      type: { required },
      DefaultDonationCategoryId: { required },
    },
  },

  data() {
    return {
      tab: 0,
      loading: false,
      loadingCategories: false,

      preDefinedValue: [],
      DonationCategories: [],
      durationTypes: ["monthly", "yearly"],

      Campaign: {
        id: null,
        name: null,
        description: null,
        type: "donation",
        DefaultDonationCategoryId: null,
        url: null,
        isActive: false,
        dontationGoal: null,
        intervalMonth: this.$moment().format("M"),
        intervalDays: this.$moment().format("D"),
        metadata: {
          preDefinedValue: [],
          donationLimit: false,
          durationType: "yearly",
          allowForever: false,
          endDate: null,
          recurredCount: null,
          showGoalFunds: true,
          showVisitorsCount: true,
          showDonateCount: false,
          detailStyle: "normal",
        },
        CampaignDetails: [],
      },

      // optional templates for reset/add
      unitTemplate: [
        {
          category: "Utilities",
          name: "Utility Units",
          amount: 20,
          totalQuantity: 0,
        },
      ],

      ramadanTemplate: [],

      editorOption: { placeholder: "Description" },
    };
  },

  computed: {
    ...mapGetters("global", ["currentUser"]),

    selectedCategoryName() {
      const id = this.Campaign.DefaultDonationCategoryId;
      const found = (this.DonationCategories || []).find((x) => x.id === id);
      return found ? found.name : "-";
    },

    nameErrors() {
      const errors = [];
      if (!this.$v.Campaign.name.$dirty) return errors;
      if (!this.$v.Campaign.name.required)
        errors.push(this.$t("validations.fieldIsRequired"));
      return errors;
    },

    DonationIdErrors() {
      const errors = [];
      if (!this.$v.Campaign.DefaultDonationCategoryId.$dirty) return errors;
      if (!this.$v.Campaign.DefaultDonationCategoryId.required)
        errors.push(this.$t("validations.fieldIsRequired"));
      return errors;
    },

    editableKeys() {
      const style = this.Campaign?.metadata?.detailStyle || "normal";

      if (style === "fast")
        return ["category", "name", "amount", "date", "day", "visitors"];
      if (style === "student")
        return ["category", "name", "amount", "totalQuantity"];
      if (style === "units")
        return ["category", "name", "amount", "totalQuantity"];

      return ["category", "name", "amount"];
    },

    detailHeaders() {
      const headers = [
        { text: "", value: "__actions", sortable: false, align: "left" },
      ];
      for (const key of this.editableKeys) {
        headers.push({
          text: this.prettyHeader(key),
          value: key,
          sortable: false,
          align:
            key === "amount" || key === "visitors" || key === "totalQuantity"
              ? "right"
              : "left",
        });
      }
      return headers;
    },
  },

  created() {
    this.getDonationCategories().then(() => {
      this.loadData();
    });
  },

  watch: {
    preDefinedValue: {
      deep: true,
      handler() {
        if (!this.Campaign.metadata) this.Campaign.metadata = {};
        this.Campaign.metadata.preDefinedValue = this.preDefinedValue;
      },
    },

    "Campaign.type"(t) {
      if (t === "sponsor" && !this.Campaign.metadata.detailStyle) {
        this.Campaign.metadata.detailStyle = "normal";
      }

      if (t === "donation") {
        // donation has no details tab
        if (this.tab === 2) this.tab = 0;
      }
    },
    "Campaign.metadata.detailStyle"() {
      this.enforceStyleRules();
    },
  },

  methods: {
    enforceStyleRules() {
      if (!this.Campaign.metadata) this.Campaign.metadata = {};

      const style = this.Campaign?.metadata?.detailStyle || "normal";

      if (style === "units") {
        this.Campaign.metadata.durationType = "monthly";
        this.Campaign.metadata.allowForever = true;
        this.Campaign.metadata.endDate = null;
        this.Campaign.metadata.recurredCount = null;
      } else {
        // do NOT override user's choice, just set defaults if missing
        if (!this.Campaign.metadata.durationType)
          this.Campaign.metadata.durationType = "yearly";
        if (this.Campaign.metadata.allowForever === undefined)
          this.Campaign.metadata.allowForever = false;
        if (this.Campaign.metadata.endDate === undefined)
          this.Campaign.metadata.endDate = null;
        if (this.Campaign.metadata.recurredCount === undefined)
          this.Campaign.metadata.recurredCount = null;
      }
    },
    // --- normalize rows so old campaigns can be edited and saved with capacity ---
    hydrateDetailsFromApi() {
      const style = this.Campaign?.metadata?.detailStyle || "normal";
      const rows = Array.isArray(this.Campaign.CampaignDetails)
        ? this.Campaign.CampaignDetails
        : [];

      this.Campaign.CampaignDetails = rows.map((r) => {
        const row = { ...(r || {}) };
        row.metadata = row.metadata || {};

        // If backend has capacity (new schema), show it as totalQuantity in edit UI
        if (row.totalQuantity === undefined) {
          const cap = row.capacity ?? row.metadata?.capacity;
          if (cap !== undefined && cap !== null) row.totalQuantity = cap;
        }

        // For reference if you want to show sold later
        if (row.soldQuantity === undefined) {
          const sold = row.soldQty ?? row.metadata?.soldQty;
          if (sold !== undefined && sold !== null) row.soldQuantity = sold;
        }

        // keep style in metadata
        row.metadata.detailStyle = row.metadata.detailStyle || style;

        return row;
      });
    },

    normalizeDetailsForApi() {
      const style = this.Campaign?.metadata?.detailStyle || "normal";
      const rows = Array.isArray(this.Campaign.CampaignDetails)
        ? this.Campaign.CampaignDetails
        : [];

      return rows.map((r) => {
        const row = { ...(r || {}) };

        const amount = Number(row.amount) || 0;

        let capacity = null;
        if (
          row.totalQuantity !== undefined &&
          row.totalQuantity !== null &&
          row.totalQuantity !== ""
        ) {
          const n = Number(row.totalQuantity);
          capacity = Number.isFinite(n) ? n : null;
        }

        // keep soldQty as-is if it exists (important for edits), else 0
        let soldQty = 0;
        if (row.soldQty !== undefined && row.soldQty !== null)
          soldQty = Number(row.soldQty) || 0;
        if (
          row.soldQuantity !== undefined &&
          row.soldQuantity !== null &&
          row.soldQuantity !== ""
        ) {
          const n = Number(row.soldQuantity);
          soldQty = Number.isFinite(n) ? n : soldQty;
        }

        const metadata = {
          ...(row.metadata || {}),
          detailStyle: style,
          date: row.date,
          day: row.day,
          visitors: row.visitors,
          pricingMode: row.pricingMode,
          capMode: row.capMode,
          allowQuantity: row.allowQuantity,
        };

        Object.keys(metadata).forEach(
          (k) => metadata[k] === undefined && delete metadata[k]
        );

        return {
          id: row.id, // ✅ keep id so backend updates instead of re-creating
          category: row.category,
          name: row.name,
          amount,

          // ✅ new columns (or backend will map if you handle in service)
          capacity,
          soldQty,

          metadata,
          delete: row.delete === true, // in case you use it backend-side
        };
      });
    },

    applyTemplate(style) {
      // On edit, templates should be intentional. "fast" resets, others add rows.

      if (style === "fast") {
        // If you want to reset existing to Ramadan template, plug it in here
        // this.Campaign.CampaignDetails = JSON.parse(JSON.stringify(this.ramadanTemplate))
        // For now, just keep it empty reset
        this.Campaign.CampaignDetails = [];
        this.tab = 2;
        return;
      }

      if (style === "student") {
        this.Campaign.CampaignDetails.push({
          category: "Students",
          name: "Student Sponsorship",
          amount: 0,
          totalQuantity: 0,
          soldQuantity: 0,
          metadata: {
            pricingMode: "unit",
            capMode: "quantity",
            allowQuantity: true,
          },
        });
        this.tab = 2;
        return;
      }

      if (style === "units") {
        this.Campaign.CampaignDetails.push({
          category: "Utilities",
          name: "Utility Units",
          amount: 20,
          totalQuantity: 0,
          soldQuantity: 0,
          metadata: {
            pricingMode: "unit",
            capMode: "quantity",
            allowQuantity: true,
          },
        });
        this.tab = 2;
        return;
      }
    },

    prettyHeader(key) {
      return String(key)
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },

    fieldType(key) {
      if (["amount", "visitors", "totalQuantity", "soldQuantity"].includes(key))
        return "number";
      if (key === "date") return "date";
      return "text";
    },

    onEditorChange({ html }) {
      this.Campaign.description = html;
    },

    removeValue(index) {
      this.preDefinedValue.splice(index, 1);
    },

    addValue() {
      this.$swal
        .fire({
          title: "Enter Value",
          input: "number",
          showCancelButton: true,
          confirmButtonText: "Save",
          allowOutsideClick: () => !this.$swal.isLoading(),
        })
        .then((result) => {
          if (result.value) this.preDefinedValue.push(result.value);
          else this.$swal.fire({ title: "Please enter valid value." });
        });
    },

    deleteItem(i) {
      this.Campaign.CampaignDetails.splice(i, 1);
    },

    addDetail(val) {
      this.Campaign.CampaignDetails.push(val);
      this.tab = 2;
    },

    pickFile() {
      this.$refs.image.click();
    },

    onFilePicked(e) {
      const { files } = e.target;
      if (!files || !files[0]) return;

      const fr = new FileReader();
      fr.readAsDataURL(files[0]);
      fr.addEventListener("load", () => {
        this.Campaign.url = fr.result;
      });
    },

    getDonationCategories() {
      this.loadingCategories = true;
      return donationCategoryService
        .getAll({ publicDonation: true })
        .then((result) => {
          this.DonationCategories = result.data || [];
          this.loadingCategories = false;
        })
        .catch(() => {
          this.loadingCategories = false;
        });
    },
    loadData() {
      const { id } = this.$route.params;
      this.loading = true;

      return campaignService
        .getById(id)
        .then((response) => {
          this.Campaign = response.data || this.Campaign;

          // always ensure metadata exists
          if (!this.Campaign.metadata) this.Campaign.metadata = {};

          // defaults (safe for old campaigns)
          if (!this.Campaign.metadata.detailStyle)
            this.Campaign.metadata.detailStyle = "normal";
          if (!this.Campaign.metadata.durationType)
            this.Campaign.metadata.durationType = "yearly";
          if (this.Campaign.metadata.allowForever === undefined)
            this.Campaign.metadata.allowForever = false;
          if (this.Campaign.metadata.endDate === undefined)
            this.Campaign.metadata.endDate = null;
          if (this.Campaign.metadata.recurredCount === undefined)
            this.Campaign.metadata.recurredCount = null;

          // apply unit rules if needed
          this.enforceStyleRules();

          this.preDefinedValue = this.Campaign.metadata.preDefinedValue || [];
          this.hydrateDetailsFromApi();

          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          console.log("error", error);
        });
    },
    close() {
      this.$router.go(-1);
    },

    submit() {
      // ✅ Always ensure metadata exists
      if (!this.Campaign.metadata) this.Campaign.metadata = {};

      // keep predefined values in metadata
      this.Campaign.metadata.preDefinedValue = this.preDefinedValue || [];

      // ✅ Validate
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
        return;
      }

      // ✅ Sponsor must have at least 1 item
      if (this.Campaign.type === "sponsor") {
        const rows = Array.isArray(this.Campaign.CampaignDetails)
          ? this.Campaign.CampaignDetails
          : [];
        if (rows.length === 0) {
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: "Please add at least 1 campaign item.",
            });
          return;
        }
      }

      // ✅ Apply units rules etc (monthly, allowForever, etc)
      this.enforceStyleRules();

      // ✅ Build payload explicitly, do NOT rely on spread to keep metadata
      const payload = {
        ...this.Campaign,
        metadata: { ...(this.Campaign.metadata || {}) }, // IMPORTANT
        CampaignDetails: this.normalizeDetailsForApi(),
      };

      this.loading = true;

      return campaignService
        .update(this.Campaign.id, payload)
        .then((result) => {
          if (result.status) {
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({ icon: "success", title: "Campaign is updated" });

            this.loading = false;
            this.close();
          } else {
            this.loading = false;
          }
        })
        .catch((err) => {
          this.loading = false;
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: err?.data?.message || "Failed to update campaign",
            });
        });
    },
  },
};
</script>

<style scoped>
.touch-view-container {
  height: calc(100vh - 48px);
  overflow: auto;
  padding-top: 12px;
}

.card-soft {
  border-radius: 16px;
  overflow: hidden;
}

.upload-card {
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
}

.upload-placeholder {
  height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.upload-preview {
  border-radius: 14px;
  overflow: hidden;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell-actions {
  width: 54px;
}
</style>